<template>
  <div class="mobile-banner-carousel" :style="{'background-color': carouselObj.bgColor}">
    <h5 class="carousel-title">{{ carouselObj.title }}</h5>
    <div 
      class="carousel-track-container"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div 
        class="carousel-track" 
        :style="trackStyle"
      >
        <div 
          v-for="(item, index) in carouselObj.list" 
          :key="index" 
          class="carousel-item"
        >
          <!-- 图片区域 -->
          <div class="item-image-wrapper">
            <img :src="item.img" :alt="item.name" class="item-img" />
          </div>
          
          <!-- 文字区域 -->
          <div class="item-content">
            <div class="item-name">{{ item.name }}</div>
            <p class="item-desc" v-html="item.desc"></p>
          </div>
        </div>
      </div>
    </div>

    <!-- 指示器 -->
    <div class="carousel-indicators">
      <span 
        v-for="(item, index) in carouselObj.list" 
        :key="index"
        class="indicator-dot"
        :class="{ active: currentIndex === index }"
        @click="goToSlide(index)"
      ></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MobileBannerCarousel',
  props: {
    carouselObj: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      autoPlay: true,
      interval: 5000, // 自动轮播间隔 ms
      currentIndex: 0,
      startX: 0,
      currentX: 0,
      isDragging: false,
      timer: null,
      containerWidth: 0
    }
  },
  computed: {
    trackStyle() {
      // 计算偏移量：当前索引 * -100% + 拖动时的偏移
      const baseTranslate = -this.currentIndex * 100;
      let dragOffset = 0;
      
      if (this.isDragging && this.containerWidth > 0) {
        // 将像素偏移转换为百分比，以便与 baseTranslate 相加
        const percentOffset = (this.currentX - this.startX) / this.containerWidth * 100;
        dragOffset = percentOffset;
      }

      return {
        transform: `translateX(${baseTranslate + dragOffset}%)`,
        transition: this.isDragging ? 'none' : 'transform 0.3s ease-out'
      };
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateContainerWidth();
      if (this.autoPlay) {
        this.startAutoPlay();
      }
    });
    
    // 监听窗口大小变化以重新计算宽度
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.stopAutoPlay();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    updateContainerWidth() {
      const container = this.$el.querySelector('.carousel-track-container');
      if (container) {
        this.containerWidth = container.offsetWidth;
      }
    },
    handleResize() {
      this.updateContainerWidth();
    },
    // --- 自动轮播控制 ---
    startAutoPlay() {
      this.stopAutoPlay();
      this.timer = setInterval(() => {
        this.next();
      }, this.interval);
    },
    stopAutoPlay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // --- 滑动逻辑 ---
    handleTouchStart(e) {
      this.stopAutoPlay(); // 触摸时停止自动播放
      this.startX = e.touches[0].clientX;
      this.currentX = this.startX;
      this.isDragging = true;
    },
    handleTouchMove(e) {
      if (!this.isDragging) return;
      this.currentX = e.touches[0].clientX;
    },
    handleTouchEnd() {
      this.isDragging = false;
      const diff = this.currentX - this.startX;
      const threshold = this.containerWidth * 0.2; // 滑动超过 20% 宽度则切换

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.prev();
        } else {
          this.next();
        }
      } else {
        // 回弹到当前位置
        // 由于 computed 中依赖 currentIndex，只需重置 drag 状态即可触发 transition 回归
      }
      
      if (this.autoPlay) {
        this.startAutoPlay();
      }
    },
    // --- 切换方法 ---
    next() {
      if (this.currentIndex < this.carouselObj.list.length - 1) {
        this.currentIndex++;
      } else {
        this.currentIndex = 0; // 循环回到第一张
      }
    },
    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else {
        this.currentIndex = this.carouselObj.list.length - 1; // 循环到最后一张
      }
    },
    goToSlide(index) {
      this.currentIndex = index;
      if (this.autoPlay) {
        this.startAutoPlay(); // 手动点击后重置计时器
      }
    }
  }
}
</script>

<style lang="less" scoped>
.mobile-banner-carousel {
  width: 100%;
  padding: 0 5.33vw; /* 两边整体间距 */
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  .carousel-title {
    font-weight: @fw1;
    font-size: 8.53vw;
    margin: 21.33vw 0 9.33vw;
  }
}

.carousel-track-container {
  width: 100%;
  overflow: hidden;
  touch-action: pan-y; /* 允许垂直滚动，拦截水平滑动 */
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
}

.carousel-track {
  display: flex;
  width: 100%;
}

.carousel-item {
  min-width: 100%; /* 每个项目占满容器宽度 */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.item-image-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  
  .item-img {
    max-width: 100%;
    height: auto;
    display: block;
    /* 可根据需求限制最大高度，例如 max-height: 40vh; */
  }
}

.item-content {
  text-align: center;
  width: 100%;
  margin-top: 9.33vw;
  color: #0A0A0A;
  font-weight: @fw2;
  
  .item-name {
    font-weight: @fw1;
    font-size: 4.8vw;
  }
  
  .item-desc {
    font-size: 3.2vw;
    margin-top: 2.67vw;
    height: 16vw;
    padding: 0 3.2vw;
  }
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  margin-top: 3.2vw;
  padding-bottom: 23.47vw;
}

.indicator-dot {
  width: 4.26vw;
  height: 2px;
  background-color: rgba(174,174,174,0.3);  
  &.active {
    background-color: #AEAEAE;
  }
}
</style>